<script setup lang="ts">
import { computed, ref, watch, watchPostEffect, type ComponentPublicInstance } from 'vue';
import RadioButton from 'primevue/radiobutton';
import InputNumber, { type InputNumberInputEvent } from 'primevue/inputnumber';
import { PlacementPriceBasisSchema } from '@neon-one/auctions-types';
import type { PlacementPriceBasis, ReassignTarget } from '@neon-one/auctions-types';

import { formatCents } from '../../format';
import { firstNameOf } from '../../reassign-target';

const PriceBasis = PlacementPriceBasisSchema.enum;
const CENTS_PER_DOLLAR = 100;
const CUSTOM_INPUT_MAX_LENGTH = 32;
const INVALID_CUSTOM = 'Enter an amount above $0.';
const CUSTOM_ERROR_ID = 'ri-price-custom-error';

type PriceTarget = Pick<ReassignTarget, 'bidderUuid' | 'name' | 'bidCents'>;

const props = withDefaults(defineProps<{
    basis: PlacementPriceBasis;
    customCents: number | null;
    selected: PriceTarget | null;
    fmvCents: number | null;
    noun?: 'item' | 'bundle';
    showErrors?: boolean;
}>(), { noun: 'item', showErrors: false });
const emit = defineEmits<{
    'update:basis': [PlacementPriceBasis];
    'update:customCents': [number | null];
}>();

const theirBid = computed(() => props.selected?.bidCents ?? null);
const hasFmv = computed(() => props.fmvCents !== null && props.fmvCents > 0);
const firstName = computed(() => firstNameOf(props.selected?.name));
const defaultBasis = computed(() => theirBid.value !== null
    ? PriceBasis.bid
    : hasFmv.value ? PriceBasis.fmv : PriceBasis.custom);
const effectiveBasis = computed(() => {
    const unavailable = (props.basis === PriceBasis.bid && theirBid.value === null)
        || (props.basis === PriceBasis.fmv && !hasFmv.value);
    return unavailable ? defaultBasis.value : props.basis;
});
const customDollars = computed(() => props.customCents === null ? null : props.customCents / CENTS_PER_DOLLAR);
const customInvalid = computed(() => effectiveBasis.value === PriceBasis.custom
    && (props.customCents === null || !Number.isInteger(props.customCents) || props.customCents <= 0));
const showCustomError = computed(() => props.showErrors && customInvalid.value);
const amountInput = ref<ComponentPublicInstance | null>(null);

watch(effectiveBasis, (basis) => {
    if (basis !== props.basis) emit('update:basis', basis);
}, { immediate: true });
watch(() => props.selected?.bidderUuid, () => {
    if (defaultBasis.value !== props.basis) emit('update:basis', defaultBasis.value);
    if (props.customCents !== null) emit('update:customCents', null);
});

// InputNumber has no native length/description props; behavioral attributes only, not a styling passthrough.
watchPostEffect(() => {
    const input = amountInput.value?.$el.querySelector('input') as HTMLInputElement | undefined;
    if (!input) return;
    input.maxLength = CUSTOM_INPUT_MAX_LENGTH;
    if (showCustomError.value) input.setAttribute('aria-describedby', CUSTOM_ERROR_ID);
    else input.removeAttribute('aria-describedby');
});

function updateAmount(dollars: number | null): void {
    emit('update:customCents', dollars === null ? null : Math.round(dollars * CENTS_PER_DOLLAR));
}

// The model only updates on blur, so the submit button would stay disabled until
// the admin left the field. Each keystroke reports the amount typed so far.
function onAmountInput(event: InputNumberInputEvent): void {
    updateAmount(typeof event.value === 'number' ? event.value : null);
}
</script>

<template>
    <fieldset class="ri__group">
        <legend class="ri__legend">
            Price
        </legend>
        <div
            v-if="theirBid !== null"
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveBasis === PriceBasis.bid }"
            data-testid="ri__price-bid"
        >
            <RadioButton
                :model-value="effectiveBasis"
                :value="PriceBasis.bid"
                input-id="ri-price-bid"
                name="ri-price"
                @update:model-value="emit('update:basis', PriceBasis.bid)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-price-bid"
            >
                <span class="ri__mode-title">Their bid ({{ formatCents(theirBid) }})</span>
                <span class="ri__mode-desc text-caption">What {{ firstName }} bid on this {{ noun }}.</span>
            </label>
        </div>
        <div
            v-if="hasFmv"
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveBasis === PriceBasis.fmv }"
            data-testid="ri__price-fmv"
        >
            <RadioButton
                :model-value="effectiveBasis"
                :value="PriceBasis.fmv"
                input-id="ri-price-fmv"
                name="ri-price"
                @update:model-value="emit('update:basis', PriceBasis.fmv)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-price-fmv"
            >
                <span class="ri__mode-title">Fair market value ({{ formatCents(fmvCents ?? 0) }})</span>
                <span class="ri__mode-desc text-caption">The {{ noun }}'s value from the catalog.</span>
            </label>
        </div>
        <div
            class="app-checkbox-group ri__mode"
            :class="{ 'ri__mode--selected': effectiveBasis === PriceBasis.custom }"
            data-testid="ri__price-custom"
        >
            <RadioButton
                :model-value="effectiveBasis"
                :value="PriceBasis.custom"
                input-id="ri-price-custom"
                name="ri-price"
                @update:model-value="emit('update:basis', PriceBasis.custom)"
            />
            <label
                class="app-field-label ri__mode-text"
                for="ri-price-custom"
            >
                <span class="ri__mode-title">Custom amount</span>
                <span class="ri__mode-desc text-caption">Any amount above $0.</span>
            </label>
        </div>
        <div
            v-if="effectiveBasis === PriceBasis.custom"
            class="app-field-group ri__custom"
        >
            <label
                class="app-field-label"
                for="ri-price-custom-amount"
            >Custom amount</label>
            <InputNumber
                ref="amountInput"
                :model-value="customDollars"
                input-id="ri-price-custom-amount"
                mode="currency"
                currency="USD"
                locale="en-US"
                placeholder="$0.00"
                fluid
                :invalid="showCustomError"
                data-testid="ri__price-custom-amount"
                @update:model-value="updateAmount"
                @input="onAmountInput"
            />
            <small
                v-if="showCustomError"
                :id="CUSTOM_ERROR_ID"
                class="app-field-helper app-field-error"
                role="alert"
                data-testid="ri__price-error"
            >{{ INVALID_CUSTOM }}</small>
        </div>
    </fieldset>
</template>

<style scoped>
.ri__group {
    margin: var(--app-space-0);
    padding: var(--app-space-0);
    border: none;
    display: flex;
    flex-direction: column;
    gap: var(--app-space-8);
}
.ri__legend {
    padding: var(--app-space-0);
    margin-bottom: var(--app-space-8);
    font-size: var(--app-font-size-body-sm);
    font-weight: var(--app-font-weight-semibold);
    color: var(--app-text-primary);
}
.ri__mode {
    display: flex;
    align-items: flex-start;
    gap: var(--app-space-12);
    padding: var(--app-space-12);
    border: var(--app-border-width-default) solid var(--app-border-muted);
    border-radius: var(--app-radius-md);
    cursor: pointer;
}
.ri__mode:hover {
    border-color: var(--app-border-hover);
}
.ri__mode--selected {
    border-color: var(--app-border-active);
    background: var(--app-highlight-background);
}
.ri__mode-text {
    display: flex;
    flex: 1;
    cursor: pointer;
    flex-direction: column;
    gap: var(--app-space-4);
    min-width: 0;
}
.ri__mode-title {
    font-weight: var(--app-font-weight-semibold);
}
.ri__mode-desc {
    color: var(--app-text-primary);
}
.app-field-group {
    display: flex;
    flex-direction: column;
    gap: var(--app-space-8);
}
.app-field-label {
    font-size: var(--app-font-size-body-sm);
    font-weight: var(--app-font-weight-medium);
    color: var(--app-text-primary);
}
.ri__custom {
    margin-left: var(--app-space-32);
    min-width: 0;
}
.app-field-helper {
    font-size: var(--app-font-size-body-sm);
    line-height: var(--app-line-height-body-sm);
    color: var(--app-text-muted);
}
.app-field-error {
    color: var(--app-invalid);
}
</style>
